// NOAA's public "NOS Hydrographic Surveys" ArcGIS REST service (gis.ngdc.noaa.gov) -- the
// official index of historical NOS surveys, each with a DOWNLOAD_URL to its NCEI landing page
// (where a survey's Final Tide Note / Tide Zoning Chart lives, when one was produced). CORS is
// open, so this is called straight from the browser like the CO-OPS provider.
const BASE = "https://gis.ngdc.noaa.gov/arcgis/rest/services/web_mercator/nos_hydro_dynamic/MapServer";

// Layer 0 = surveys with BAGs, 1 = with other digital sounding data, 2 = no digital data.
const LAYERS = [
  { id: 0, label: "Has BAG" },
  { id: 1, label: "Has digital soundings" },
  { id: 2, label: "No digital data" },
];

const FIELDS = "SURVEY_ID,SURVEY_YEAR,LOCALITY,SUBLOCALITY,PLATFORM,DATE_SURVEY_END,DOWNLOAD_URL,DIGITAL_DATA";

function toYear(epochMs) {
  const n = Number(epochMs);
  if (!Number.isFinite(n)) return null;
  const d = new Date(n);
  return Number.isFinite(d.getTime()) ? d.getUTCFullYear() : null;
}

async function queryLayer(layer, south, west, north, east, limit) {
  const q = new URLSearchParams({
    geometry: `${west},${south},${east},${north}`,
    geometryType: "esriGeometryEnvelope",
    inSR: "4326",
    spatialRel: "esriSpatialRelIntersects",
    outFields: FIELDS,
    returnGeometry: "false",
    resultRecordCount: String(limit),
    f: "json",
  });
  const res = await fetch(`${BASE}/${layer.id}/query?${q.toString()}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (data?.error) throw new Error(data.error.message || "ArcGIS query error");
  const rows = Array.isArray(data?.features) ? data.features : [];
  return rows.map((f) => {
    const a = f.attributes || {};
    return {
      surveyId: String(a.SURVEY_ID || ""),
      year: Number.isFinite(a.SURVEY_YEAR) ? a.SURVEY_YEAR : toYear(a.DATE_SURVEY_END),
      locality: a.LOCALITY || "",
      sublocality: a.SUBLOCALITY || "",
      platform: a.PLATFORM || "",
      digitalData: a.DIGITAL_DATA || "",
      downloadUrl: a.DOWNLOAD_URL || null,
      layer: layer.label,
    };
  });
}

// Finds NOS surveys intersecting a bbox (lon/lat), newest first, searching all three coverage
// layers. Each hit links to NOAA's own per-survey landing page (DOWNLOAD_URL) -- the authoritative
// place to find that survey's tide note/zoning chart if one exists, so we never have to guess a
// file path.
export async function findSurveysInBbox(south, west, north, east, limitPerLayer = 60) {
  const sets = await Promise.all(LAYERS.map((layer) => queryLayer(layer, south, west, north, east, limitPerLayer).catch(() => [])));
  const seen = new Map();
  sets.flat().forEach((s) => {
    if (!s.surveyId) return;
    const prev = seen.get(s.surveyId);
    if (!prev || (s.year || 0) > (prev.year || 0)) seen.set(s.surveyId, s);
  });
  return [...seen.values()].sort((a, b) => (b.year || 0) - (a.year || 0));
}
